/** 교정 diff 렌더 — 문서 본문(교정 표·문단)과 패널 상세가 같이 쓴다 */
import type { DsdEditorSpellcheckBlock } from "@/apis/dsd-editor/route";
import { cn } from "@/lib/utils";
import {
  getCorrectionCategory,
  getSpellcheckCorrectedSegments,
  getSpellcheckOriginalSegments,
  isVisibleWhitespace,
  type SpellcheckDiffSegment,
} from "@/utils/dsd-editor/spellcheck";
import { Fragment, type ReactNode, useMemo } from "react";

// 변경 세그먼트 색+테두리 — 원문은 빨강, 교정문은 초록 (ring 방식)
export const CHANGE_RED = "rounded-[3px] ring-1 ring-red-500 bg-red-100";
export const CHANGE_RED_WS = "text-status-negative";
export const CHANGE_GREEN =
  "rounded-[3px] ring-1 ring-green-500 bg-green-100 text-label-normal";
export const CHANGE_GREEN_WS = "text-status-positive";

const whitespaceGlyph = "·";

const WhitespaceGlyph = ({
  changed,
  changedWhitespaceClassName,
}: {
  changed: boolean;
  changedWhitespaceClassName: string;
}) => (
  <span
    className={cn(
      changed ? changedWhitespaceClassName : "text-label-assistive opacity-72",
    )}
  >
    {whitespaceGlyph}
  </span>
);

// 변경 세그먼트는 어절 통째로 하나의 박스(테두리/배경)여야 한다. 박스는 바깥 span에서
// 걸고, 여기선 내부 공백만 글리프로 치환한다(각 조각에 박스를 걸면 18·개월처럼 쪼개짐).
const renderDiffSegmentText = (
  text: string,
  changed: boolean,
  changedWhitespaceClassName: string,
) => {
  const nodes: ReactNode[] = [];
  let pendingText = "";

  const flushPendingText = () => {
    if (!pendingText) return;

    nodes.push(<Fragment key={`text-${nodes.length}`}>{pendingText}</Fragment>);
    pendingText = "";
  };

  for (const char of Array.from(text)) {
    if (!isVisibleWhitespace(char)) {
      pendingText += char;
      continue;
    }

    flushPendingText();
    nodes.push(
      <WhitespaceGlyph
        key={`space-${nodes.length}`}
        changed={changed}
        changedWhitespaceClassName={changedWhitespaceClassName}
      />,
    );
  }

  flushPendingText();

  return nodes;
};

export const DiffText = ({
  segments,
  changedClassName,
  changedWhitespaceClassName,
}: {
  segments: SpellcheckDiffSegment[];
  changedClassName: string;
  changedWhitespaceClassName: string;
}) => (
  <>
    {segments.map((segment, index) => (
      <span
        key={`${index}-${segment.text}`}
        className={cn(
          segment.changed && "box-decoration-clone",
          segment.changed && changedClassName,
        )}
      >
        {renderDiffSegmentText(
          segment.text,
          segment.changed,
          changedWhitespaceClassName,
        )}
      </span>
    ))}
  </>
);

export const DsdReviewCorrectionInlineDiff = ({
  block,
  showOriginal = true,
}: {
  block: DsdEditorSpellcheckBlock;
  /** false면 원문(red diff) 생략 — 편집 중 참고 표시용(칩+교정문만) */
  showOriginal?: boolean;
}) => {
  // 하이라이트 구간은 백엔드 changes의 range 그대로 (어절 단위)
  const originalSegments = useMemo(
    () => getSpellcheckOriginalSegments(block),
    [block],
  );
  const correctedSegments = useMemo(
    () => getSpellcheckCorrectedSegments(block),
    [block],
  );

  return (
    <div className="flex flex-col gap-2">
      {showOriginal && (
        <p className="label-2-medium whitespace-pre-wrap text-label-alternative">
          <DiffText
            segments={originalSegments}
            changedClassName={CHANGE_RED}
            changedWhitespaceClassName={CHANGE_RED_WS}
          />
        </p>
      )}
      <span className="inline-flex w-fit items-center rounded-[6px] bg-green-100 px-1.5 py-0.5 caption-1-medium text-emerald-600">
        교정 완료
      </span>
      <p className="label-2-medium whitespace-pre-wrap text-label-normal">
        <DiffText
          segments={correctedSegments}
          changedClassName={CHANGE_GREEN}
          changedWhitespaceClassName={CHANGE_GREEN_WS}
        />
      </p>
    </div>
  );
};

export const DsdReviewCorrectionCellDiff = ({
  block,
  showOriginal = true,
}: {
  block: DsdEditorSpellcheckBlock;
  /** false면 원문(red diff) 생략 — 편집 중 참고 표시용(칩+교정문만) */
  showOriginal?: boolean;
}) => {
  // 하이라이트 구간은 백엔드 changes의 range 그대로 (어절 단위)
  const originalSegments = useMemo(
    () => getSpellcheckOriginalSegments(block),
    [block],
  );
  const correctedSegments = useMemo(
    () => getSpellcheckCorrectedSegments(block),
    [block],
  );

  return (
    <span className="flex min-w-0 flex-col gap-1">
      {showOriginal && (
        <span className="whitespace-pre-wrap text-label-alternative">
          <DiffText
            segments={originalSegments}
            changedClassName={CHANGE_RED}
            changedWhitespaceClassName={CHANGE_RED_WS}
          />
        </span>
      )}
      <span className="w-fit rounded-[6px] bg-green-100 px-1.5 py-0.5 caption-1-medium text-emerald-600">
        교정 완료
      </span>
      <span className="block whitespace-pre-wrap text-label-normal">
        <DiffText
          segments={correctedSegments}
          changedClassName={CHANGE_GREEN}
          changedWhitespaceClassName={CHANGE_GREEN_WS}
        />
      </span>
    </span>
  );
};

const CorrectionCategoryIcon = () => (
  <svg
    className="size-3 shrink-0"
    viewBox="0 0 13 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M6.43294 11.0996C6.43294 10.1079 5.67461 9.34961 4.68294 9.34961H0.599609V0.599609H4.09961C5.38294 0.599609 6.43294 1.64961 6.43294 2.93294M6.43294 11.0996V2.93294M6.43294 11.0996C6.43294 10.1079 7.19128 9.34961 8.18294 9.34961H12.2663V8.00794M6.43294 2.93294C6.43294 1.64961 7.48294 0.599609 8.76628 0.599609H12.2663V2.34961M8.76628 5.84961L9.93294 7.01628L12.2663 4.68294"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** 맞춤법(빨강) / 띄어쓰기(주황) 구분 칩 — 텍스트 왼쪽 아이콘 */
export const DsdReviewCorrectionCategoryChip = ({
  block,
}: {
  block: DsdEditorSpellcheckBlock;
}) => {
  const isSpelling = getCorrectionCategory(block) === "spelling";

  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1 rounded-[6px] px-1.5 py-0.5 caption-1-medium",
        isSpelling
          ? "bg-rose-100 text-rose-500"
          : "bg-orange-100 text-orange-500",
      )}
    >
      <CorrectionCategoryIcon />
      {isSpelling ? "맞춤법" : "띄어쓰기"}
    </span>
  );
};
